import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, UserCheck, XCircle, Ban, CalendarClock, ArrowRight,
  ClipboardCheck, AlertTriangle, Sparkles, DollarSign, Clock, ExternalLink, Trash2,
} from "lucide-react";
import api from "@/lib/api";
import { fmtDate, money } from "@/lib/format";

const STATUS_TINT = {
  pending: "bg-[#E0A458]/15 text-[#B5794E]",
  confirmed: "bg-[#144F75]/10 text-[#144F75]",
  checked_in: "bg-[#5C8A72]/15 text-[#5C8A72]",
  in_session: "bg-[#9E5C7F]/15 text-[#9E5C7F]",
  completed: "bg-[#5C8A72]/20 text-[#4A7360]",
  no_show: "bg-[#B45151]/15 text-[#B45151]",
  needs_center_review: "bg-[#B45151]/15 text-[#B45151]",
  reschedule_proposed: "bg-[#9E5C7F]/15 text-[#9E5C7F]",
};

const NEEDS_META = {
  approval_requests: { label: "Awaiting approval", tab: "Appointments", tint: "text-[#B5794E]", Icon: ClipboardCheck },
  provider_review: { label: "Provider review", tab: "Appointments", tint: "text-[#B45151]", Icon: AlertTriangle },
  reschedule_pending: { label: "Reschedule pending", tab: "Appointments", tint: "text-[#9E5C7F]", Icon: CalendarClock },
  unassigned: { label: "No provider assigned", tab: "Appointments", tint: "text-[#B45151]", Icon: AlertTriangle },
  unpaid_holds: { label: "Unpaid / deposit due", tab: "Bookings", tint: "text-[#B5794E]", Icon: DollarSign },
  overdue_followups: { label: "Follow-ups", tab: "Appointments", tint: "text-[#144F75]", Icon: Clock },
};

const HEALTH_META = {
  services: { label: "Services", tab: "Services" },
  events: { label: "Events & Classes", tab: "Events" },
  products: { label: "Products", tab: "Orders" },
  packages: { label: "Packages", tab: "Packages" },
};

function Pill({ status }) {
  return <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_TINT[status] || STATUS_TINT.pending}`}>{(status || "").replace(/_/g, " ")}</span>;
}

function ApptRow({ b, onAction, onOpen, onDelete, acting }) {
  const canStatus = !["cancelled", "completed"].includes(b.status);
  const paid = Number(b.amount_paid || 0) > 0 || ["paid", "deposit", "partial", "balance_due"].includes(b.payment_status);
  const canDelete = b.status === "pending" && !paid;
  const A = ({ label, status, Icon, cls }) => (
    <button title={label} disabled={acting} onClick={() => onAction(b.id, status)} data-testid={`ops-action-${status}-${b.id}`}
      className={`h-8 w-8 grid place-items-center rounded-lg disabled:opacity-40 ${cls}`}><Icon size={15} /></button>
  );
  return (
    <li className="py-2.5 flex items-center justify-between gap-3 border-b border-[#F1EEE8] last:border-0" data-testid={`ops-appt-${b.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[#1A242B] truncate">
          <span className="tabular-nums text-[#8FA89B] mr-2">{b.start_time || "—"}</span>{b.service_title || "Appointment"}
        </div>
        <div className="text-xs text-[#4A5D6B] truncate">{b.name || b.user_email || "—"}{b.enhancer_name ? ` · ${b.enhancer_name}` : (b.enhancer_id ? "" : " · no provider")}{b.center_name ? ` · ${b.center_name}` : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Pill status={b.status} />
        {canStatus && (
          <div className="flex gap-1">
            {b.status === "pending" && <A label="Approve" status="confirmed" Icon={CheckCircle2} cls="text-[#144F75] hover:bg-[#144F75]/10" />}
            <A label="Check in" status="checked_in" Icon={UserCheck} cls="text-[#5C8A72] hover:bg-[#5C8A72]/10" />
            <A label="Complete" status="completed" Icon={CheckCircle2} cls="text-[#4A7360] hover:bg-[#5C8A72]/10" />
            <A label="No-show" status="no_show" Icon={XCircle} cls="text-[#B5794E] hover:bg-[#D98E73]/10" />
            <A label="Cancel (keeps history)" status="cancelled" Icon={Ban} cls="text-[#B45151] hover:bg-[#B45151]/10" />
          </div>
        )}
        {canDelete && onDelete && (
          <button title="Delete (unconfirmed & unpaid only)" disabled={acting} onClick={() => onDelete(b.id)} data-testid={`ops-delete-${b.id}`}
            className="h-8 w-8 grid place-items-center rounded-lg text-[#B45151] hover:bg-[#B45151]/10 disabled:opacity-40"><Trash2 size={14} /></button>
        )}
        <button title="Open / reschedule" onClick={() => onOpen(b.id)} data-testid={`ops-open-${b.id}`}
          className="h-8 w-8 grid place-items-center rounded-lg text-[#4A5D6B] hover:bg-[#E5E0D8]"><ExternalLink size={14} /></button>
      </div>
    </li>
  );
}

export default function OperationsHome({ center, onOpenAppointment, onGoTab }) {
  const qc = useQueryClient();
  const [seg, setSeg] = useState("today");
  const [openBucket, setOpenBucket] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["ops-dashboard", center],
    queryFn: () => api.get("/manager/operations-dashboard").then((r) => r.data),
    refetchInterval: 60000,
  });

  const act = useMutation({
    mutationFn: ({ id, status }) => api.put(`/manager/appointments/${id}/status`, { status }),
    onSuccess: (_r, v) => { toast.success(`Marked ${v.status.replace(/_/g, " ")}`); qc.invalidateQueries({ queryKey: ["ops-dashboard", center] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update"),
  });
  const onAction = (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this appointment? History, payment and payout records are preserved.")) return;
    act.mutate({ id, status });
  };

  const del = useMutation({
    mutationFn: (id) => api.delete(`/manager/appointments/${id}`),
    onSuccess: () => { toast.success("Appointment deleted"); qc.invalidateQueries({ queryKey: ["ops-dashboard", center] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete"),
  });
  const onDelete = (id) => {
    if (!window.confirm("Permanently delete this appointment? Only unconfirmed, unpaid records can be deleted — this cannot be undone.")) return;
    del.mutate(id);
  };

  if (isLoading) return <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#144F75]" /></div>;
  if (!data || data.no_center) return <div className="eyw-card p-10 text-center text-[#4A5D6B]">No center assigned yet.</div>;

  const k = data.kpis || {};
  const totalNeeds = Object.values(data.needs_counts || {}).reduce((a, b) => a + b, 0);
  const bh = data.business_health || {};
  const totalFlags = ["services", "events", "products", "packages"].reduce((a, key) => a + (bh[key]?.flagged?.length || 0), 0);

  const SEGS = [
    { k: "today", l: "Today" },
    { k: "needs", l: `Needs Attention${totalNeeds ? ` (${totalNeeds})` : ""}` },
    { k: "business", l: `Business${totalFlags ? ` · ${totalFlags} ⚠` : ""}` },
  ];

  return (
    <div data-testid="mgr-operations">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-serif text-3xl text-[#1A242B]">Operations</h1>
          <p className="text-sm text-[#4A5D6B]">{data.location?.name} · {fmtDate(data.date)}</p>
        </div>
        <div className="inline-flex rounded-full bg-[#F1EEE8] p-0.5" data-testid="ops-segment-toggle">
          {SEGS.map((s) => (
            <button key={s.k} onClick={() => setSeg(s.k)} data-testid={`ops-seg-${s.k}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${seg === s.k ? "bg-[#144F75] text-white" : "text-[#4A5D6B] hover:bg-[#E5E0D8]"}`}>{s.l}</button>
          ))}
        </div>
      </div>

      {/* KPI strip — always visible */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { l: "Today's bookings", v: k.today_total },
          { l: "Checked in", v: k.checked_in },
          { l: "Completed", v: k.completed },
          { l: "No-shows", v: k.no_show },
          { l: "Today revenue", v: `${money(Number(k.today_revenue || 0))}` },
        ].map((s) => (
          <div key={s.l} className="eyw-card p-4" data-testid={`ops-kpi-${s.l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-2xl font-serif text-[#144F75]">{s.v ?? 0}</div>
            <div className="text-xs text-[#8FA89B]">{s.l}</div>
          </div>
        ))}
      </div>

      {seg === "today" && (
        <div className="eyw-card p-6" data-testid="ops-today">
          <h2 className="font-serif text-xl text-[#1A242B] mb-3">Today's schedule</h2>
          {data.today.length === 0 ? (
            <p className="text-[#4A5D6B] text-sm py-8 text-center" data-testid="ops-today-empty">No appointments scheduled today.</p>
          ) : (
            <ul>{data.today.map((b) => <ApptRow key={b.id} b={b} acting={act.isPending || del.isPending} onAction={onAction} onDelete={onDelete} onOpen={onOpenAppointment} />)}</ul>
          )}
        </div>
      )}

      {seg === "needs" && (
        <div className="space-y-4" data-testid="ops-needs">
          {Object.entries(NEEDS_META).map(([key, meta]) => {
            const items = data.needs_attention?.[key] || [];
            const M = meta.Icon;
            const open = openBucket === key;
            return (
              <div key={key} className="eyw-card overflow-hidden" data-testid={`ops-bucket-${key}`}>
                <button onClick={() => setOpenBucket(open ? "" : key)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`ops-bucket-toggle-${key}`}>
                  <span className={`flex items-center gap-2 font-semibold ${meta.tint}`}><M size={17} /> {meta.label}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#1A242B]">{items.length}</span>
                    <button onClick={(e) => { e.stopPropagation(); onGoTab(meta.tab); }} className="text-xs text-[#144F75] hover:underline inline-flex items-center gap-1" data-testid={`ops-bucket-open-${key}`}>Open {meta.tab} <ArrowRight size={12} /></button>
                  </span>
                </button>
                {open && items.length > 0 && (
                  <ul className="px-4 pb-3">
                    {items.slice(0, 25).map((b) => (
                      <ApptRow key={b.id} b={b} acting={act.isPending || del.isPending} onAction={onAction} onDelete={onDelete} onOpen={onOpenAppointment} />
                    ))}
                  </ul>
                )}
                {open && items.length === 0 && <p className="px-4 pb-4 text-sm text-[#8FA89B]">Nothing here — all clear.</p>}
              </div>
            );
          })}
        </div>
      )}

      {seg === "business" && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="ops-business">
          {Object.entries(HEALTH_META).map(([key, meta]) => {
            const block = bh[key] || { active: 0, flagged: [] };
            return (
              <div key={key} className="eyw-card p-5" data-testid={`ops-health-${key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Sparkles size={15} className="text-[#5C8A72]" /> {meta.label}</h3>
                  <span className="text-xs text-[#8FA89B]">{block.active} active · {block.flagged.length} to fix</span>
                </div>
                {block.flagged.length === 0 ? (
                  <p className="text-sm text-[#5C8A72] flex items-center gap-1.5"><CheckCircle2 size={14} /> All set</p>
                ) : (
                  <ul className="space-y-2">
                    {block.flagged.slice(0, 8).map((it) => (
                      <li key={it.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`ops-flag-${it.id}`}>
                        <span className="text-[#1A242B] truncate">{it.title || "(untitled)"}</span>
                        <span className="flex items-center gap-1 shrink-0">
                          {it.issues.map((iss) => <span key={iss} className="text-[10px] px-1.5 py-0.5 rounded bg-[#B45151]/10 text-[#B45151]">missing {iss}</span>)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <button onClick={() => onGoTab(meta.tab)} className="mt-4 text-sm text-[#144F75] hover:underline inline-flex items-center gap-1" data-testid={`ops-health-open-${key}`}>Manage {meta.label} <ArrowRight size={13} /></button>
              </div>
            );
          })}
          <div className="eyw-card p-5 md:col-span-2" data-testid="ops-health-registrations">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg text-[#1A242B]">New registrations (7 days)</h3>
              <span className="text-2xl font-serif text-[#144F75]">{bh.new_registrations || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
